import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, throwError, timer } from 'rxjs';
import { Router } from '@angular/router';
import { AuthData } from '../login/auth.model';
import {
  RemoteUserSessionData,
  UserSessionData,
} from '../meeting/sessiondata.model';
import {
  VideoCallConfigurations,
  AudioCallConfigurations,
  ValidateRoomData,
} from '../meeting/validateroom.model';

export interface error_msg {
  heading: string;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  url = environment.backendUrl;
  hostdtls: BehaviorSubject<AuthData>;
  validation: ValidateRoomData;
  remote_video: any = null;
  newOffer: boolean = false;
  makeOffer: boolean = false;
  pVt: MediaStreamTrack;
  localVideoRecording: boolean = false;
  remoteVideoRecording: boolean = false;
  isScreenShare: boolean = false;
  video_call_constraints = {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      sampleRate: 44100,
    },
    video: {
      facingMode: 'user',
    },
  };
  audio_call_constraints = {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      sampleRate: 44100,
    },
  };
  displayMediaOptions = {
    video: {
      cursor: 'always',
    },
  };
  config = {
    iceServers: [],
  };
  peerConnections = {};
  timer = 0; // seconds
  intervalId = 0;
  timer$?: Observable<string>;
  video_call_configurations: VideoCallConfigurations;
  audio_call_configurations: AudioCallConfigurations;
  client_datapoint: Array<any> = [];
  self_datapoint: Array<any> = [];
  session_details: UserSessionData = {
    session_id: null,
    calltype: null,
    roomid: null,
    user_name: null,
    audio_status: false,
    video_status: false,
    ishost: false,
    socket_id: null,
  };

  local_view_data: RemoteUserSessionData = {
    id: null,
    uname: null,
    room: null,
    videoStatus: false,
    audioStatus: false,
    host: false,
  };

  remote_view_data: RemoteUserSessionData = {
    id: null,
    uname: null,
    room: null,
    videoStatus: false,
    audioStatus: false,
    host: false,
  };

  localVideoStream: BehaviorSubject<MediaStream>;
  remoteVideoStream: BehaviorSubject<MediaStream>;
  switchVideos: BehaviorSubject<boolean>;
  showScreenShare: BehaviorSubject<boolean>;
  errorPage: BehaviorSubject<error_msg>;

  constructor(private http: HttpClient, private router: Router) {
    this.hostdtls = new BehaviorSubject<AuthData>(null);
    this.localVideoStream = new BehaviorSubject<MediaStream>(null);
    this.remoteVideoStream = new BehaviorSubject<MediaStream>(null);
    this.switchVideos = new BehaviorSubject<boolean>(false);
    this.showScreenShare = new BehaviorSubject<boolean>(false);
    this.errorPage = new BehaviorSubject<error_msg>({
      heading: 'Something Went Wrong!',
      message: 'The meeting link you requested is invalid or expired.',
    });
  }

  public get hostDtls(): AuthData {
    if (localStorage.getItem('hostdtls')) {
      let host_dtls = JSON.parse(localStorage.getItem('hostdtls'));
      this.hostdtls.next(host_dtls);
      return host_dtls.session_id;
    } else {
      return null;
    }
  }

  getMettingData(payload: any, org_id: string, token: string) {
    const apiUrl = this.url + '/call/' + org_id;
    let http_Headers = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('Authorization', `Bearer ${token}`);
    return this.http.post(apiUrl, payload, { headers: http_Headers }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  validateRoomSession(session_id: string, room_id: string) {
    const apiUrl = this.url + `/validateRoom/${session_id}`;
    let http_Headers = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('roomid', room_id);
    return this.http.get(apiUrl, { headers: http_Headers }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error) => {
        if (error.status === 400) {
          localStorage.clear();
          const data = window.sessionStorage.getItem('appData');
          window.sessionStorage.clear();
          if (data) {
            sessionStorage.setItem('appData', data);
          }
        }
        return throwError(() => error);
      })
    );
  }

  createprofile(
    payload: any,
    session_id: string,
    host: boolean,
    room_id: string
  ) {
    const apiUrl = this.url + `/session/${session_id}/${host}`;
    let http_Headers = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('roomid', room_id);
    return this.http.post(apiUrl, payload, { headers: http_Headers }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  destroyRoomSession(session_id: string, room_id: string) {
    const apiUrl = this.url + `/destroyRoom/${session_id}`;
    let http_Headers = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('roomid', room_id);
    return this.http.get(apiUrl, { headers: http_Headers }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  postEmotionalAiStats(payload: any, profile_id: string) {
    const apiUrl = this.url + `/emotional-ai/${profile_id}`;
    let http_Headers = new HttpHeaders().set(
      'content-type',
      'application/json'
    );
    return this.http.post(apiUrl, payload, { headers: http_Headers }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  startTimer(): void {
    this.timer$ = timer(0, 1000).pipe(
      map((seconds) => this.getSecondsAsDigitalClock(seconds))
    );
  }

  stopTimer(): void {
    this.timer$ = undefined;
  }

  getSecondsAsDigitalClock(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    const hoursStr = `${hours < 10 ? '0' : ''}${hours}`;
    const minutesStr = `${minutes < 10 ? '0' : ''}${minutes}`;
    const secondsStr = `${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;

    return `${hoursStr}:${minutesStr}:${secondsStr}`;
  }

  feedbackSubmit(payload: any, profile_id: string) {
    const apiUrl = this.url + `/feedback/${profile_id}`;
    let http_Headers = new HttpHeaders().set(
      'content-type',
      'application/json'
    );
    return this.http.post(apiUrl, payload, { headers: http_Headers }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  handleRemoteHangup(id) {
    if (this.peerConnections[id]) {
      this.peerConnections[id].close();
      delete this.peerConnections[id];
    }
  }

  setTime() {
    if (this.peerConnections) {
      this.startTimer();
    }
  }

  createUserProfile(user_name: string, sessionid: string, roomid: string) {
    let payload = {
      first_name: user_name,
      last_name: ' ',
      socket_id: this.session_details.socket_id,
    };
    this.createprofile(
      payload,
      sessionid,
      this.session_details.ishost,
      roomid
    ).subscribe({
      next: (res) => {
        sessionStorage.setItem('covniaIO.webrtc.profileId', res.profile._id);
        sessionStorage.setItem('covniaIO.webrtc.is_host', res.profile.is_host);
      },
      error: (err) => {
        console.log('profile creation failed', err);
      },
    });
  }

  createBrowserSessionOnCall() {
    let date_start;
    let runningtrueStatus;
    runningtrueStatus = true;
    date_start = new Date(Date.now());
    const data = window.sessionStorage.getItem('appData');
    window.sessionStorage.clear();
    if (data) {
      sessionStorage.setItem('appData', data);
    }
    window.sessionStorage.setItem(
      'covniaIO.webrtc.sessionId',
      this.session_details.session_id
    );
    window.sessionStorage.setItem(
      'covniaIO.webrtc.name',
      this.session_details.user_name
    );
    window.sessionStorage.setItem('covniaIO.webrtc.started_on', date_start);
    window.sessionStorage.setItem('covniaIO.webrtc.running', runningtrueStatus);
  }
}
